// Core
import React from 'react';

// Libraries
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import { convertTemperature } from '../../../../../../plugins/general';
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  isEquipmentRunning,
  lubricationFlowStatusToTagType,
  lubricationFlowStatusToText,
  overallStatusToTagType,
  overallStatusToText,
  runningEnabledStatusToTagType,
  runningEnabledStatusToText,
} from '../../../../../../plugins/crusher';
import { TEMPERATURE_RANGE } from '../../../../../../plugins/thresholds';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Types
import { Theme } from '../../../../../../types/theme';
import { MeasurementType, TemperatureType } from '../../../../../../types/global';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import { ModuleHeader } from '../../../../../../layout/elements/ModuleHeader';
import { Row } from '../../../../../../layout/elements/Row';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Thermometer } from '../../../../../../layout/elements/Thermometer';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';

// View
import {
  CoolerFanStatusContainer,
  LubricationContainer,
  LubricationContent,
  OverallStatusContainer,
  ReturnTempContainer,
  SupplyTempContainer,
  TankTempContainer,
  WinterModeStatusContainer,
} from './Lubrication.style';
import { LubricationProps } from './Lubrication.types';

function Lubrication({
  className,
  unitData,
  edgeStatus,
  onOpenDrawer,
  testId,
}: LubricationProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs, isSm } = useBreakpoint();

  const userTemperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);

  return (
    <LubricationContainer
      data-testid={testId}
      className={className}
    >
      <LubricationContent>
        <Row>
          <Col>
            <ModuleHeader
              onClick={onOpenDrawer}
              title={t('view.crusher.vsi.lubrication.title')}
              testId={`${testId}-module-header`}
            />
          </Col>
        </Row>
        <Row
          style={{
            borderBottom: `1px solid ${theme.color.crusherDashboard.borderColor}`,
            borderTop: `1px solid ${theme.color.crusherDashboard.borderColor}`,
          }}
        >

          {/* Overall Status */}
          <Col sm={8} xs={24}>
            <OverallStatusContainer>
              <Paragraph
                size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                weight={ParagraphWeight.medium}
                color={theme.color.crusherDashboard.statusTitleColor}
                preventOverflow
              >
                {t('view.crusher.vsi.lubrication.overallStatus')}
              </Paragraph>
              <Tag
                text={overallStatusToText(unitData?.lubricationOverallStatus)}
                type={overallStatusToTagType(
                  unitData?.lubricationOverallStatus,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                testId="overall-status-tag"
              />
            </OverallStatusContainer>
          </Col>

          {/* Tank Heater Status */}
          <Col sm={8} xs={12}>
            <WinterModeStatusContainer>
              <Paragraph
                size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                weight={ParagraphWeight.medium}
                color={theme.color.crusherDashboard.statusTitleColor}
                preventOverflow
              >
                {
                  (isXs || isSm)
                    ? t('view.crusher.vsi.lubrication.tankHeaterMobile')
                    : t('view.crusher.vsi.lubrication.tankHeater')
                }
              </Paragraph>
              <Tag
                text={runningEnabledStatusToText(unitData?.tankLubeHeaterStatus)}
                type={runningEnabledStatusToTagType(
                  unitData?.tankLubeHeaterStatus,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                className="status"
                testId="tank-heater-status"
              />
            </WinterModeStatusContainer>
          </Col>

          {/* Supply Flow Status */}
          <Col sm={8} xs={12}>
            <CoolerFanStatusContainer>
              <Paragraph
                size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                weight={ParagraphWeight.medium}
                color={theme.color.crusherDashboard.statusTitleColor}
                preventOverflow
              >
                {t('view.crusher.vsi.lubrication.supplyFlow')}
              </Paragraph>
              <Tag
                text={lubricationFlowStatusToText(unitData?.lubricationSupplyFlowStatus)}
                type={lubricationFlowStatusToTagType(
                  unitData?.lubricationSupplyFlowStatus,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                className="status"
                testId="supply-flow-status-tag"
              />
            </CoolerFanStatusContainer>
          </Col>
        </Row>

        {/* Speedometers */}
        <Row style={{ flex: 1 }}>

          {/* Return Temperature */}
          {/* If lubeSupplyTempEnable is false, should occupy 12 columns */}
          <Col
            cols={unitData?.lubeSupplyTempEnable ? 8 : 12}
          >
            <ReturnTempContainer>
              <Thermometer
                className="thermometer"
                value={
                  convertTemperature(
                    unitData?.lubricationReturnTemperature,
                    userTemperatureUnit,
                    1,
                  ).value
                }
                minValue={
                  _.isNil(unitData?.lubricationReturnTemperature)
                    ? null
                    : TEMPERATURE_RANGE(userTemperatureUnit).minValue
                }
                maxValue={
                  _.isNil(unitData?.lubricationReturnTemperature)
                    ? null
                    : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
                }
                unit={userTemperatureUnit}
                title={`${t('view.crusher.vsi.lubrication.returnTemp')}`}
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
                thresholds={{
                  lowAlarm: convertTemperature(
                    unitData?.spLubeReturnTempLowAlarm,
                    userTemperatureUnit,
                  ).value,
                  lowWarning: convertTemperature(
                    unitData?.spLubeReturnTempLowWarning,
                    userTemperatureUnit,
                  ).value,
                  highWarning: convertTemperature(
                    unitData?.spLubeReturnTempHighWarn,
                    userTemperatureUnit,
                  ).value,
                  highAlarm: convertTemperature(
                    unitData?.spLubeReturnTempStopFeed,
                    userTemperatureUnit,
                  ).value,
                }}
                testId="return-temperature-thermometer"
              />
            </ReturnTempContainer>
          </Col>

          {/* Tank Temperature */}
          {/* If lubeSupplyTempEnable is false, should occupy 12 columns */}
          <Col
            cols={unitData?.lubeSupplyTempEnable ? 8 : 12}
          >
            <TankTempContainer>
              <Thermometer
                className="thermometer"
                value={
                  convertTemperature(
                    unitData?.lubricationTankTemperature,
                    userTemperatureUnit,
                    1,
                  ).value
                }
                minValue={
                  _.isNil(unitData?.lubricationTankTemperature)
                    ? null
                    : TEMPERATURE_RANGE(userTemperatureUnit).minValue
                }
                maxValue={
                  _.isNil(unitData?.lubricationTankTemperature)
                    ? null
                    : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
                }
                unit={userTemperatureUnit}
                title={`${t('view.crusher.vsi.lubrication.tankTemp')}`}
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
                thresholds={{
                  lowAlarm: convertTemperature(
                    unitData?.spLubeTankTempLowAlarm,
                    userTemperatureUnit,
                  ).value,
                  lowWarning: convertTemperature(
                    unitData?.spLubeTankTempLowWarning,
                    userTemperatureUnit,
                  ).value,
                  highWarning: convertTemperature(
                    unitData?.spLubeTankTempHighWarning,
                    userTemperatureUnit,
                  ).value,
                  highAlarm: convertTemperature(
                    unitData?.spLubeTankTempHighAlarm,
                    userTemperatureUnit,
                  ).value,
                }}
                testId="tank-temperature-thermometer"
              />
            </TankTempContainer>
          </Col>

          {/* Supply Temperature */}
          {unitData?.lubeSupplyTempEnable && (
            <Col cols={8}>
              <SupplyTempContainer>
                <Thermometer
                  className="thermometer"
                  value={
                    convertTemperature(
                      unitData?.lubricationSupplyTemperature,
                      userTemperatureUnit,
                      1,
                    ).value
                  }
                  minValue={
                    _.isNil(unitData?.lubricationSupplyTemperature)
                      ? null
                      : TEMPERATURE_RANGE(userTemperatureUnit).minValue
                  }
                  maxValue={
                    _.isNil(unitData?.lubricationSupplyTemperature)
                      ? null
                      : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
                  }
                  unit={userTemperatureUnit}
                  title={`${t('view.crusher.vsi.lubrication.supplyTemp')}`}
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  testId="supply-temperature-thermometer"
                />
              </SupplyTempContainer>
            </Col>
          )}
        </Row>

      </LubricationContent>
    </LubricationContainer>
  );
}

export { Lubrication };
